import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, List, ListItem, ListItemText, Tooltip, Typography } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { Item } from '../../common/Rest/Services/Data';
import { useCart } from '../../../contexts/CartContext';
import wishListService from '../../common/Rest/Services/WishListService/WishListService';
import { useAuth } from '../../common/Auth/AuthProvider';
import ROUTES from '../../common/Routing/Routes';
import { getBookImageSrcSet, getBookImageUrl } from '../../common/Utils/BookImages';
import BookCoverFrame from '../../common/BookCoverFrame/BookCoverFrame';
import { ApiError } from '../../common/Rest/ReaderlyHttpClient';
import { showErrorSnackbar } from '../../common/Utils/SnackbarService';
import waitlistService, { WaitlistStatus } from '../../common/Rest/Services/WaitlistService/WaitlistService';
import './HomePage.css';

interface ItemGridProps {
  items: Item[];
  gridColumns: number;
  lastItemRef: React.Ref<HTMLLIElement | null>;
  /** Whether a fixed-width sidebar (e.g. HomePage's 300px filter panel) shares the row with the grid. */
  hasSidebar?: boolean;
  itemBadges?: Record<number, React.ReactNode>;
  actionMode?: 'rent' | 'notify';
  rentButtonLabel?: string;
}

const priceLabel = (item: Item) => `${item.price} грн`;

// Content area caps out at the page's max-width (1440px), minus the sidebar (when present)
// and the page/card gutters, so the `sizes` hint matches what each column actually renders at —
// otherwise the browser picks a lower-res srcSet entry than the slot needs and covers look blurry.
const buildSizes = (gridColumns: number, hasSidebar: boolean) => {
  const contentWidth = 1440 - (hasSidebar ? 300 : 0) - 32;
  const columnWidth = Math.round(contentWidth / Math.max(gridColumns, 1)) - 16;
  return `(max-width: 600px) calc((100vw - 32px - 14px) / 2), (max-width: 900px) calc((100vw - 48px) / 2), ${columnWidth}px`;
};

const isItemAvailable = (item: Item) => item.stockCount === undefined || item.stockCount > 0;

const ItemGrid: React.FC<ItemGridProps> = ({
  items,
  gridColumns,
  lastItemRef,
  hasSidebar = false,
  itemBadges = {},
  actionMode = 'rent',
  rentButtonLabel,
}) => {
  const { addToCart, isInCart, openCartModal } = useCart();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [wishListMap, setWishListMap] = useState<Record<number, boolean>>({});
  const [waitlistMap, setWaitlistMap] = useState<Record<number, WaitlistStatus | undefined>>({});
  const [waitlistSubmittingMap, setWaitlistSubmittingMap] = useState<Record<number, boolean>>({});
  const isAdmin = user?.role === 'Admin';

  useEffect(() => {
    const initialMap: Record<number, boolean> = {};
    items.forEach((item) => {
      initialMap[item.id] = item.isInWishList ?? false;
    });
    setWishListMap(initialMap);
  }, [items]);

  useEffect(() => {
    // In rent mode, books with every copy out get a "notify me" button instead of a dead
    // "rent" one, so their queue status is needed too.
    const queueItems = actionMode === 'notify' ? items : items.filter((item) => !isItemAvailable(item));
    if (!queueItems.length || !isAuthenticated) {
      setWaitlistMap({});
      return;
    }

    let ignore = false;
    Promise.all(
      queueItems.map(async (item) => {
        try {
          return [item.id, await waitlistService.GetStatus(item.id)] as const;
        } catch {
          return [item.id, undefined] as const;
        }
      })
    ).then((entries) => {
      if (!ignore) setWaitlistMap(Object.fromEntries(entries));
    });

    return () => {
      ignore = true;
    };
  }, [actionMode, isAuthenticated, items]);

  const handleToggleWish = async (itemId: number) => {
    if (!isAuthenticated) {
      navigate(ROUTES.LOGIN);
      return;
    }

    const currentlyInWish = wishListMap[itemId] === true;

    try {
      if (currentlyInWish) {
        await wishListService.DeleteFromWishList(itemId);
        setWishListMap((prev) => ({ ...prev, [itemId]: false }));
      } else {
        await wishListService.AddToWishlist(itemId);
        setWishListMap((prev) => ({ ...prev, [itemId]: true }));
      }
    } catch (error) {
      // The server treats add/remove as already-in-sync (400) when our local state was
      // stale — reconcile silently instead of showing a confusing error for a non-issue.
      if (error instanceof ApiError && error.status === 400) {
        setWishListMap((prev) => ({ ...prev, [itemId]: !currentlyInWish }));
      } else {
        console.error('WishList toggle failed:', error);
        showErrorSnackbar('Не вдалося оновити вішліст. Спробуйте ще раз.');
      }
    }
  };

  const handleToggleWaitlist = async (itemId: number) => {
    if (!isAuthenticated) {
      navigate(ROUTES.LOGIN);
      return;
    }

    if (waitlistSubmittingMap[itemId]) return;
    setWaitlistSubmittingMap((prev) => ({ ...prev, [itemId]: true }));

    try {
      if (waitlistMap[itemId]?.inQueue) {
        await waitlistService.Leave(itemId);
      } else {
        await waitlistService.Join(itemId);
      }

      const status = await waitlistService.GetStatus(itemId);
      setWaitlistMap((prev) => ({ ...prev, [itemId]: status }));
    } catch {
      showErrorSnackbar('Не вдалося оновити чергу. Спробуйте ще раз.');
    } finally {
      setWaitlistSubmittingMap((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  return (
    <List
      className="items-grid"
      sx={{
        display: 'grid',
        gap: { xs: '14px', sm: '16px' },
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          sm: 'repeat(2, minmax(0, 1fr))',
          md: `repeat(${Math.min(gridColumns, 3)}, minmax(0, 1fr))`,
          lg: `repeat(${gridColumns}, minmax(0, 1fr))`,
        },
        gridAutoRows: '1fr',
        padding: 0,
      }}
    >
      {items.map((item, index) => {
        const inCart = isInCart(item.id);
        const inWish = wishListMap[item.id] === true;
        const isAvailable = isItemAvailable(item);
        const showNotify = actionMode === 'notify' || (!isAvailable && !inCart);
        const waitlistStatus = waitlistMap[item.id];
        const adminEditUrl = `${ROUTES.ADMIN}?tab=books&bookId=${item.id}`;
        const seriesLabel = item.seriesNumber != null
          ? item.series?.name
            ? `${item.series.name} · #${item.seriesNumber}`
            : `Серія · #${item.seriesNumber}`
          : null;

        return (
          <ListItem
            className="list-item book-card"
            key={item.id}
            ref={index === items.length - 1 ? lastItemRef : null}
          >
            {itemBadges[item.id] && <span className="book-card-badge">{itemBadges[item.id]}</span>}

            {isAdmin && (
              <Tooltip title="Редагувати книгу">
                <IconButton
                  aria-label={`Редагувати ${item.name} в адмінці`}
                  className="book-card-admin-edit"
                  component="a"
                  href={adminEditUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                  size="small"
                  onClick={(event) => event.stopPropagation()}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <Link to={`${ROUTES.DETAILS}/${item.id}`} className="book-card-cover-link">
              <BookCoverFrame
                src={getBookImageUrl(item, 'medium')}
                srcSet={getBookImageSrcSet(item)}
                sizes={buildSizes(gridColumns, hasSidebar)}
                alt={item.name}
                className="book-card-cover-frame"
                imageClassName="book-card-cover"
              />
            </Link>

            <Box className="book-card-body">
              <ListItemText
                primary={item.name}
                primaryTypographyProps={{ className: 'book-card-title' }}
              />
              {seriesLabel && (
                <Typography className="book-card-series-number">{seriesLabel}</Typography>
              )}
              {item.description && (
                <Typography className="book-card-description">{item.description}</Typography>
              )}

              <Box className="book-card-meta">
                <Typography className="book-card-price">{priceLabel(item)}</Typography>
                <Typography className={isAvailable ? 'stock-ok' : 'stock-empty'}>
                  {isAvailable ? 'Доступна' : 'Усі примірники в оренді'}
                </Typography>
              </Box>
            </Box>

            <Box className="book-card-actions">
              <Button component={Link} to={`${ROUTES.DETAILS}/${item.id}`} variant="outlined">
                Детальніше
              </Button>

              {showNotify ? (
                <Button
                  variant={waitlistStatus?.inQueue ? 'contained' : 'outlined'}
                  startIcon={waitlistStatus?.inQueue ? <NotificationsActiveIcon /> : <NotificationsNoneIcon />}
                  title={waitlistStatus?.inQueue ? 'Вийти з черги' : 'Повідомимо, коли книжка звільниться'}
                  disabled={Boolean(waitlistSubmittingMap[item.id])}
                  onClick={() => handleToggleWaitlist(item.id)}
                >
                  {waitlistStatus?.inQueue ? 'У черзі' : 'Сповістити'}
                </Button>
              ) : (
                <Button
                  variant={inCart ? 'contained' : 'outlined'}
                  startIcon={<MenuBookIcon />}
                  onClick={() => {
                    if (inCart) {
                      openCartModal();
                    } else {
                      addToCart(item, 1);
                    }
                  }}
                >
                  {inCart ? 'До кошика' : rentButtonLabel ?? 'Орендувати'}
                </Button>
              )}

              <Tooltip title={!isAuthenticated ? 'Увійдіть, щоб додати у вішліст' : 'Вішліст'}>
                <IconButton
                  size="large"
                  aria-label={inWish ? 'Прибрати з вішліста' : 'Додати у вішліст'}
                  onClick={() => handleToggleWish(item.id)}
                  className={inWish ? 'wishlist-active' : ''}
                >
                  {inWish ? <FavoriteIcon color="error" /> : <FavoriteBorderIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </ListItem>
        );
      })}
    </List>
  );
};

export default ItemGrid;
